import { faCopy, faTrashCan } from '@fortawesome/free-regular-svg-icons'
import {
  faCheckCircle,
  faExclamationTriangle,
  faPencil,
  faRotateRight,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import CoptyTextWrapper from '../../../../../components/CopyTextWrapper'
import Tooltip from '../../../../../components/Tooltip'
import type {
  NetworkDto,
  WalletHostingType,
  WalletScreeningStatus,
} from '../../../../../types/coreApi-types'

interface WalletItemProps {
  network?: NetworkDto | null
  name?: string | null
  address: string
  destinationTag?: string | null
  hostingType?: WalletHostingType
  screeningStatus?: WalletScreeningStatus | null
  createdAt?: string
  onPressDelete: () => void
  onPressEdit?: () => void
  onPressRetryScreening?: () => void
  onPressVerify?: () => void
}

const screeningBadgeConfig: Record<
  WalletScreeningStatus,
  { icon: typeof faCheckCircle; iconClass: string; textClass: string; label: string; spin?: boolean; retryable?: boolean }
> = {
  Pending: { icon: faExclamationTriangle, iconClass: 'text-yellow-600 dark:text-yellow-400', textClass: 'text-yellow-700 dark:text-yellow-300', label: 'Verification required' },
  Approved: { icon: faCheckCircle, iconClass: 'text-green-600 dark:text-green-400', textClass: 'text-green-700 dark:text-green-300', label: 'Verified' },
  Rejected: { icon: faTimesCircle, iconClass: 'text-red-600 dark:text-red-400', textClass: 'text-red-700 dark:text-red-300', label: 'Rejected', retryable: true },
  Error: { icon: faExclamationTriangle, iconClass: 'text-orange-600 dark:text-orange-400', textClass: 'text-orange-700 dark:text-orange-300', label: 'Screening error', retryable: true },
}

const ScreeningBadge: React.FC<{
  status: WalletScreeningStatus
  onRetry?: () => void
  onVerify?: () => void
}> = ({ status, onRetry, onVerify }) => {
  const config = screeningBadgeConfig[status]
  if (!config) return null

  return (
    <div className="flex items-center gap-1.5">
      <FontAwesomeIcon
        icon={config.icon}
        className={`w-3 h-3 ${config.iconClass} ${config.spin ? 'animate-spin' : ''}`}
      />
      <span className={`text-xs ${config.textClass}`}>{config.label}</span>
      {status === 'Pending' && onVerify && (
        <button
          onClick={e => {
            e.stopPropagation()
            onVerify()
          }}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline ml-1 font-medium"
        >
          Verify
        </button>
      )}
      {config.retryable && onRetry && (
        <button
          onClick={e => {
            e.stopPropagation()
            onRetry()
          }}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline ml-1"
        >
          <FontAwesomeIcon icon={faRotateRight} className="w-3 h-3 mr-0.5" />
          Retry
        </button>
      )}
    </div>
  )
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return null
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export const WalletItem: React.FC<WalletItemProps> = ({
  network,
  name,
  address,
  destinationTag,
  hostingType,
  screeningStatus,
  createdAt,
  onPressDelete,
  onPressEdit,
  onPressRetryScreening,
  onPressVerify,
}) => {
  const iconClass =
    'h-9 w-9 flex items-center justify-center cursor-pointer rounded-md text-brand-light-text-description dark:text-brand-dark-text-subtle-description group hover:bg-brand-light-background dark:hover:bg-brand-dark-background'

  const iconRed = 'hover:text-brand-light-red dark:hover:text-brand-dark-red'
  const iconBlue = 'hover:text-brand-accent dark:hover:text-brand-accent'
  const iconYellow = 'hover:text-yellow-600 dark:hover:text-yellow-400'

  const isUnknownHostingType = hostingType === 'Unknown' || !hostingType
  const needsVerification = !screeningStatus

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <img
            className="h-9 w-9 rounded-full"
            src={network?.iconUrl}
            alt="Network"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semi">{name || address}</p>
              {isUnknownHostingType && (
                <Tooltip label="Wallet hosting type not declared. Click edit to declare it.">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="w-4 h-4 text-yellow-600 dark:text-yellow-400"
                  />
                </Tooltip>
              )}
            </div>
            {name && <p className="text-description-subtle">{address}</p>}
            {destinationTag && (
              <p className="text-description-subtle">{`${destinationTag}`}</p>
            )}
            <div className="flex items-center gap-2">
              <p className="text-description">{`${network?.name}`}</p>
              {createdAt && (
                <span className="text-description-subtle text-xs">
                  {formatDate(createdAt)}
                </span>
              )}
              {screeningStatus ? (
                <ScreeningBadge
                  status={screeningStatus}
                  onRetry={onPressRetryScreening}
                  onVerify={onPressVerify}
                />
              ) : onPressRetryScreening ? (
                <div className="flex items-center gap-1.5">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="w-3 h-3 text-yellow-600 dark:text-yellow-400"
                  />
                  <span className="text-xs text-yellow-700 dark:text-yellow-300">Not verified</span>
                  <button
                    onClick={e => {
                      e.stopPropagation()
                      onPressRetryScreening()
                    }}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline ml-1 font-medium"
                  >
                    Start verification
                  </button>
                </div>
              ) : null}
            </div>
            {isUnknownHostingType && (
              <div className="mt-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-2">
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="w-3 h-3 mr-1 inline"
                  />
                  Declare hosting type to use this wallet
                </p>
              </div>
            )}
            {needsVerification && !isUnknownHostingType && (
              <div className="mt-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-2">
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="w-3 h-3 mr-1 inline"
                  />
                  Verify this wallet to use it for transactions
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {onPressEdit && (
            <Tooltip
              label={
                isUnknownHostingType
                  ? 'Declare wallet hosting type'
                  : 'Edit wallet hosting type'
              }
            >
              <div
                onClick={onPressEdit}
                className={`${iconClass} ${iconYellow}`}
              >
                <FontAwesomeIcon icon={faPencil} />
              </div>
            </Tooltip>
          )}
          <Tooltip label="Delete wallet">
            <div onClick={onPressDelete} className={`${iconClass} ${iconRed}`}>
              <FontAwesomeIcon icon={faTrashCan} />
            </div>
          </Tooltip>

          <Tooltip label="Copy address" closeOnClick={true}>
            <CoptyTextWrapper text={address}>
              <div className={`${iconClass} ${iconBlue}`}>
                <FontAwesomeIcon icon={faCopy} />
              </div>
            </CoptyTextWrapper>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
